import { fetchGraphql, FetchGraphqlOptions } from '@zoopla/fetch-graphql';
import { setupLogger } from '@zoopla/logger';
import { formatPostcode, LOGGER_METADATA } from '../../../helpers';
import {
    AddressesByPostcodeDocument,
    AddressesByPostcodeQuery,
    AddressesByPostcodeQueryVariables,
} from '../queries/addressesByPostcode.generated';

const logger = setupLogger(LOGGER_METADATA);

export type Address = {
    fullAddress: string;
    latitude: number;
    longitude: number;
    outcode: string;
    postcode: string;
    uprn: string;
};

export const getAddressesForPostcode = async (
    postcode: string,
    options?: FetchGraphqlOptions
): Promise<Map<Address['uprn'], Address> | null> => {
    if (!postcode) return null;

    // AddressesByPostcodeQuery requires a postcode with a space, so
    // we have to format it here to ensure that we don't get errors for
    // valid (but unformatted) postcodes
    const variables = {
        postcode: formatPostcode(postcode),
    };

    const { data, errors } = await fetchGraphql<
        AddressesByPostcodeQuery,
        AddressesByPostcodeQueryVariables
    >(AddressesByPostcodeDocument, variables, options);

    if (errors) {
        const errorMessage = errors.map((e) => e.message).join(',');
        logger.error({ err: errorMessage }, 'There was an error');
        throw new Error(
            `There was an error getting addresses: ${errorMessage}`
        );
    }

    const addressesMap: Map<Address['uprn'], Address> = new Map();
    const addresses = data?.addressesByPostcode?.addresses ?? [];

    for (const address of addresses) {
        // TODO - Future: Move to an isValidAddress function
        if (!address) continue;
        const { fullAddress, latitude, longitude, outcode, postcode, uprn } =
            address;

        if (
            !fullAddress?.trim() ||
            typeof latitude !== 'number' ||
            typeof longitude !== 'number' ||
            !outcode?.trim() ||
            !postcode?.trim() ||
            !uprn?.trim()
        ) {
            continue;
        }

        addressesMap.set(address.uprn, {
            fullAddress,
            latitude,
            longitude,
            outcode,
            postcode,
            uprn,
        });
    }

    return addressesMap;
};
